import { clsx } from 'clsx';
import { ListBoxItem } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import Icon from '~/components/data-display/Icon';
import Radio from '~/components/inputs/Radio/Radio';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { useDocumentLabel } from '~/hooks/documents/useDocumentLabel';
import IconArrowTrendDown from '~/icons/icon-arrow-trend-down.svg?react';
import IconArrowTrendUp from '~/icons/icon-arrow-trend-up.svg?react';
import { Document } from '~/types/document';
import { Money } from '~/types/money';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import { formatMoney, isNotZero } from '~/utils/money';
import styles from './DocumentSearchCard.module.scss';
import { documentPaid, documentOutstandingBalance } from './documentBalance';

interface BaseProps {
  amount?: Money;
  document: Document;
  variant?: 'compact' | 'default';
}

type Props =
  | (BaseProps & {
      selectionMode: 'listbox';
    })
  | (BaseProps & {
      selectionMode?: 'radio';
    });

const DocumentSearchCard = (props: Props) => {
  const { amount, document, variant = 'default' } = props;
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.associate_document' });
  const TrendIcon = document.isInflow ? IconArrowTrendUp : IconArrowTrendDown;
  const documentLabel = useDocumentLabel();
  const isCompact = variant === 'compact';

  const totalPaid = isCompact ? null : documentPaid(document);
  const outstandingBalance = isCompact ? null : documentOutstandingBalance(document);
  const displayAmount = amount ?? document.grossAmount;
  const textValue = [
    document.counterpart?.companyName ?? document.counterpartName,
    document.format,
    document.number,
    document.creationDate,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <VStack gap={isCompact ? 0.5 : 2} className={styles.content}>
      <HStack gap={2} justify="space-between" align={isCompact ? 'center' : 'start'}>
        <VStack gap={0.5}>
          <Text variant="body2" weight="semibold">
            {document.counterpart?.companyName ?? document.counterpartName}
          </Text>
          <Text variant="caption" weight="medium" secondaryColor>
            {documentLabel(document)}
          </Text>
        </VStack>
        <VStack gap={1} align="end">
          <div className={styles.amountWithIcon}>
            <Text variant="body1" weight="semibold" tabularNums>
              {formatMoney(displayAmount)}
            </Text>
            <Icon component={TrendIcon} size="sm" className={document.isInflow ? styles.trendUp : styles.trendDown} />
          </div>
          {totalPaid && outstandingBalance && isNotZero(totalPaid) && (
            <VStack align="end">
              <Text variant="caption" weight="medium">
                {t('paid')}:{' '}
                <Text variant="caption" weight="semibold" tabularNums>
                  {formatMoney(totalPaid)}
                </Text>
              </Text>
              <Text variant="caption" weight="medium">
                {t('to_pay')}:{' '}
                <Text variant="caption" weight="semibold" tabularNums>
                  {formatMoney(outstandingBalance)}
                </Text>
              </Text>
            </VStack>
          )}
        </VStack>
      </HStack>
      {!isCompact && (
        <div className={styles.flowsList}>
          {document.flows?.map((flow, index) => (
            <span key={index}>
              <Text variant="body2" weight="regular">
                {t('flow', { index: index + 1, date: formatFromISOToDisplay(flow.expectedPaymentDate) })}
              </Text>
              <Text variant="body2" weight="semibold">
                {formatMoney(flow.amount)}
              </Text>
            </span>
          ))}
        </div>
      )}
    </VStack>
  );

  if (props.selectionMode === 'listbox') {
    return (
      <ListBoxItem
        id={document.id}
        textValue={textValue}
        className={clsx(styles.radioCard, styles.listBoxCard, isCompact && styles.compactCard)}
      >
        <div className={styles.listBoxIndicatorContainer} aria-hidden="true">
          <div className={styles.listBoxIndicator}></div>
        </div>
        {content}
      </ListBoxItem>
    );
  }

  return (
    <Radio value={document.id} className={clsx(styles.radioCard, isCompact && styles.compactCard)}>
      {content}
    </Radio>
  );
};

export default DocumentSearchCard;
